/**
 * 편성 카드의 손짓: **누르면 상세, 끌면 옮기기.**
 *
 * 처음에는 누르면 맞은편 칸으로 옮기고 길게 누르면 상세를 열었다. 상세를 보려면 오래 눌러야 했고, 잘못 누르면
 * 병사가 옮겨 갔다. 플레이한 사람이 청했다: "톡 누르면 상세, 이동은 상세창에서 하거나 드래그로."
 *
 * - 손가락: 카드 줄은 옆으로 밀어 굴린다. 그래서 바로 끌면 굴리기와 섞인다. **잠깐(0.3초) 누르고 있으면 카드를
 *   집고**, 그때부터 끄는 대로 따라온다. 누른 채 10px 넘게 움직이면(굴리는 중) 집지 않는다. 집은 뒤에는
 *   `touchmove`를 막아 화면이 따라 굴러가지 않게 한다.
 * - **손가락은 터치 이벤트로 받는다.** 처음에는 포인터 이벤트로 받았는데, 폰(삼성 인터넷)에서 집히지 않았다
 *   (플레이한 사람이 짚었다). 안드로이드 브라우저는 길게 누르기를 제 몸짓으로 알아보면 포인터 이벤트를
 *   `pointercancel`로 끊는다. 터치 이벤트는 그래도 이어지고, 손가락이 카드 밖으로 나가도 카드로 온다 — 끄는
 *   동안 잡기(pointer capture)도 필요 없다. PC 엣지의 터치 흉내에서는 끊기지 않아 시험이 통과했었다.
 * - 마우스·펜: 포인터 이벤트. 누른 채 6px 넘게 움직이면 곧바로 집는다. 마우스는 끌어서 굴리지 않는다.
 * - 집었다가 움직이지 않고 떼면 누르기로 친다(상세).
 * - 끌고 놓은 직후의 click은 삼킨다 — 안 삼키면 놓자마자 상세가 열린다.
 * - 오른쪽 클릭(메뉴 키·Shift+F10 포함)은 누르기와 같다(상세). 손가락으로 길게 누를 때 뜨는 메뉴는 막는다.
 */
const HOLD_MS = 300;
const SLOP_TOUCH = 10;
const SLOP_MOUSE = 6;
const SWALLOW_MS = 400;
/** 집은 카드를 키우는 배율(들어 올린 느낌). */
const GHOST_SCALE = 1.1;

export interface CardDrag {
  /** 끄는 동안. 놓일 곳을 칠하는 데 쓴다. */
  readonly move: (x: number, y: number) => void;
  /** 놓았다. 화면 좌표. */
  readonly drop: (x: number, y: number) => void;
  /** 끌기를 거뒀다(취소·놓기 모두 뒤에 부른다). */
  readonly end: () => void;
}

export function hookCard(node: HTMLElement, tap: () => void, drag: CardDrag | null): void {
  let timer = 0;
  let x0 = 0;
  let y0 = 0;
  let ghost: HTMLElement | null = null;
  let dx = 0;
  let dy = 0;
  let droppedAt = -Infinity;
  /** 집은 뒤 움직였나. 안 움직이고 떼면 누르기다. */
  let moved = false;
  /** 지금 누르고 있는 손가락(터치 식별자). 없으면 -1. */
  let finger = -1;
  /** 마우스 포인터. 없으면 -1. */
  let mouse = -1;
  /** 방금 손가락이 닿았나. 손가락 뒤에 따라오는 contextmenu를 걸러낸다. */
  let touchedAt = -Infinity;

  const clearTimer = (): void => {
    clearTimeout(timer);
    timer = 0;
  };

  /**
   * 집은 자리(dx, dy)가 손가락·커서 밑에 오게 옮기고, 그 자리를 축으로 1.1배 키운다. 처음에는 CSS `scale: 1.1`로 키웠는데,
   * 따로 쓴 `scale`은 `transform`보다 바깥에 걸려 옮긴 거리까지 1.1배가 됐다 — 카드가 화면 왼쪽 위에서 멀수록 커서에서
   * 떨어졌다(1280 화면에서 약 30px, 넓은 화면 오른쪽 아래에서는 그 몇 배. 플레이한 사람이 짚었다).
   */
  const place = (x: number, y: number): void => {
    if (!ghost) return;
    ghost.style.transform = `translate(${x - dx}px, ${y - dy}px) scale(${GHOST_SCALE})`;
  };

  const pickUp = (x: number, y: number): void => {
    if (!drag || ghost) return;
    const r = node.getBoundingClientRect();
    dx = x - r.left;
    dy = y - r.top;
    ghost = node.cloneNode(true) as HTMLElement;
    ghost.classList.add('drag-ghost');
    ghost.removeAttribute('data-key');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.width = `${r.width}px`;
    ghost.style.height = `${r.height}px`;
    ghost.style.transformOrigin = `${dx}px ${dy}px`;
    // 화면(#screen)에 붙인다. 테마 토큰(밝은 판)이 거기 걸려 있어, body에 붙이면 밝은 테마에서도 어두운 카드가 떴다.
    (node.closest('#screen') ?? document.body).append(ghost);
    place(x, y);
    node.classList.add('dragging');
    moved = false;
    try {
      navigator.vibrate?.(12);
    } catch {
      // 떨림을 못 내도 끌기는 된다.
    }
    drag.move(x, y);
  };

  const finish = (dropAt: { x: number; y: number } | null): void => {
    clearTimer();
    if (!ghost) return;
    ghost.remove();
    ghost = null;
    node.classList.remove('dragging');
    droppedAt = performance.now();
    if (dropAt) drag?.drop(dropAt.x, dropAt.y);
    drag?.end();
  };

  // ---- 손가락: 터치 이벤트 ----
  const touchOf = (e: TouchEvent): Touch | null => {
    for (let k = 0; k < e.changedTouches.length; k++) {
      if (e.changedTouches[k].identifier === finger) return e.changedTouches[k];
    }
    return null;
  };
  node.addEventListener(
    'touchstart',
    (e) => {
      touchedAt = performance.now();
      if (e.touches.length > 1) {
        // 두 손가락(확대 등)이면 거둔다.
        finish(null);
        finger = -1;
        return;
      }
      // 손가락이 하나뿐인데 앞 손가락이 남아 있으면 그 touchend를 잃은 것이다. 거두고 새 손가락을 받는다 — 그냥
      // 돌아가면 이번 한 번은 집을 수 없었다(리뷰가 짚었다).
      if (finger !== -1) finish(null);
      const t = e.changedTouches[0];
      finger = t.identifier;
      x0 = t.clientX;
      y0 = t.clientY;
      clearTimer();
      if (drag) {
        timer = window.setTimeout(() => {
          timer = 0;
          pickUp(x0, y0);
        }, HOLD_MS);
      }
    },
    { passive: true },
  );
  node.addEventListener(
    'touchmove',
    (e) => {
      const t = touchOf(e);
      if (!t) return;
      if (ghost) {
        // 브라우저가 이미 굴리기를 시작했으면(취소할 수 없는 touchmove) 막을 수 없다. 끌기를 거둔다 — 안 그러면
        // 줄은 굴러가는데 유령만 따라왔다.
        if (!e.cancelable) {
          finish(null);
          return;
        }
        // 집은 뒤에는 화면이 굴러가지 않게 한다. 처리기를 처음부터 카드에 달아 두어야 막힌다 — 집는 순간 문서에
        // 달면 iOS(WebKit)는 touchstart 때 정한 대로 굴리기를 시작한다(리뷰가 짚었다).
        if (e.cancelable) e.preventDefault();
        if (Math.hypot(t.clientX - x0, t.clientY - y0) > SLOP_TOUCH) moved = true;
        place(t.clientX, t.clientY);
        drag?.move(t.clientX, t.clientY);
        return;
      }
      // 굴리기가 이미 시작됐으면(취소할 수 없다) 집지 않는다. 브라우저의 터치 문턱(약 8px)이 우리 문턱(10px)보다
      // 작아, 0.3초 안에 9px쯤 흘러가면 줄이 굴러가기 시작한 뒤에 집혔다(리뷰가 짚었다).
      if (timer !== 0 && (!e.cancelable || Math.hypot(t.clientX - x0, t.clientY - y0) > SLOP_TOUCH)) clearTimer();
    },
    { passive: false },
  );
  node.addEventListener('touchend', (e) => {
    const t = touchOf(e);
    if (!t) return;
    finger = -1;
    if (!ghost) {
      clearTimer();
      return;
    }
    // 끌기로 끝났다. 뒤따르는 흉내 click을 막는다(`finish`의 삼키기가 한 번 더 막는다).
    if (e.cancelable) e.preventDefault();
    if (moved) {
      finish({ x: t.clientX, y: t.clientY });
    } else {
      // 집기만 하고 떼었다 — 누르기다.
      finish(null);
      tap();
    }
  });
  node.addEventListener('touchcancel', () => {
    finger = -1;
    finish(null);
  });

  // ---- 마우스: 포인터 이벤트 ----
  node.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch' || e.button !== 0) return;
    mouse = e.pointerId;
    x0 = e.clientX;
    y0 = e.clientY;
  });
  node.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch' || e.pointerId !== mouse) return;
    if (ghost) {
      place(e.clientX, e.clientY);
      drag?.move(e.clientX, e.clientY);
      return;
    }
    if (drag && (e.buttons & 1) === 1 && Math.hypot(e.clientX - x0, e.clientY - y0) > SLOP_MOUSE) {
      try {
        node.setPointerCapture(e.pointerId);
      } catch {
        mouse = -1;
        return;
      }
      // 잡은 자리는 누른 곳(x0, y0)으로 잰다. 집히는 순간(6px 넘게 움직인 뒤)의 자리로 쟀더니, 빠르게 끌면 카드가 그만큼
      // 커서에서 밀려 있었다(Todo8: 커서에 카드가 붙지 않는다). 그 뒤 지금 자리로 옮긴다.
      pickUp(x0, y0);
      place(e.clientX, e.clientY);
      drag?.move(e.clientX, e.clientY);
      // 마우스는 끌어야 집히므로 집힌 것이 곧 움직인 것이다. 손가락 문턱(10px)으로 재면 7–9px 끌어 놓은 것이
      // 누르기가 되어 상세가 열렸다(리뷰가 짚었다).
      moved = true;
    }
  });
  node.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'touch' || e.pointerId !== mouse) return;
    mouse = -1;
    if (ghost) finish({ x: e.clientX, y: e.clientY });
  });
  node.addEventListener('lostpointercapture', (e) => {
    if (e.pointerType !== 'touch' && ghost) finish(null);
  });
  // 집기 전에는 잡지 않으므로, 카드 가장자리에서 미끄러져 밖에서 떼면 pointerup이 여기로 오지 않는다. 벗어나면
  // 되돌린다 — 안 그러면 남은 pointerId로 나중에 누른 채 지나갈 때 집혔다(리뷰가 짚었다).
  node.addEventListener('pointerleave', (e) => {
    if (e.pointerType !== 'touch' && !ghost) mouse = -1;
  });

  node.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    // 손가락으로 길게 누르면 브라우저가 contextmenu를 낸다 — 그것은 집기다. 마우스 오른쪽 클릭·메뉴 키만 상세.
    if (ghost || performance.now() - touchedAt < 1500) return;
    tap();
  });
  node.addEventListener('click', () => {
    if (performance.now() - droppedAt < SWALLOW_MS) return;
    tap();
  });
}
